import { CanAccess, useInvalidate, useNotification } from '@refinedev/core'
import type { Cabinet, Credits } from '@sirius/contracts/admin'
import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import type { FormEvent } from 'react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { formatCredits } from '~/i18n/format'
import { RECORD_ACTIVITY_RESOURCE } from '~/providers/dataProvider'
import {
  creditActionsFor,
  creditErrorDescription,
  creditInAmountsText,
  isCabinetCreditRefusal,
  parseCreditAmount,
} from './cabinetCredits'

/**
 * The cabinet page's credit section: add credits (an amount typed into a
 * dialog), cash out the whole balance (behind a confirmation). Only for those who may
 * (`canCreditCabinets`: staff, the host tenant's owners and admins); members
 * see the balance in the details only.
 */
export function CabinetCreditActions({ cabinet }: { cabinet: Cabinet }) {
  return (
    <CanAccess
      resource="cabinets"
      action="credit"
      fallback={null}
    >
      <CreditSection cabinet={cabinet} />
    </CanAccess>
  )
}

function CreditSection({ cabinet }: { cabinet: Cabinet }) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const invalidate = useInvalidate()
  const { open } = useNotification()
  const { disabledReason, canCreditIn, canCashOut } = creditActionsFor(
    cabinet,
    t,
  )

  const refresh = () =>
    Promise.all([
      invalidate({
        resource: 'cabinets',
        invalidates: ['list', 'detail'],
        id: cabinet.id,
      }),
      // the show page's History lists the new event
      invalidate({
        resource: RECORD_ACTIVITY_RESOURCE,
        invalidates: ['list'],
      }),
    ])

  // the cabinet's state changed under the page: show it (the actions, the
  // new balance)
  const refreshAfterRefusal = (error: Error) => {
    if (isCabinetCreditRefusal(error)) {
      void invalidate({
        resource: 'cabinets',
        invalidates: ['detail'],
        id: cabinet.id,
      })
    }
  }

  const creditIn = useMutation({
    mutationFn: (amount: Credits) =>
      client.cabinets.creditIn({ id: cabinet.id, amount }),
    onSuccess: async (_cabinet, amount) => {
      await refresh()
      open?.({
        type: 'success',
        message: t('cabinetsPage.credits.creditedIn', {
          name: cabinet.name,
          credits: formatCredits(amount),
        }),
      })
    },
    // the dialog stays open and shows why
    onError: refreshAfterRefusal,
  })

  const cashOut = useMutation({
    // the balance the operator confirmed: the API pays out exactly that or
    // nothing (CABINET_CREDITS_CHANGED), so the toast can name it
    mutationFn: (credits: Credits) =>
      client.cabinets.cashOut({ id: cabinet.id, credits }),
    onSuccess: async (_cabinet, paidOut) => {
      await refresh()
      open?.({
        type: 'success',
        message: t('cabinetsPage.credits.cashedOut', {
          name: cabinet.name,
          credits: formatCredits(paidOut),
        }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('cabinetsPage.credits.cashOutFailed'),
        description: creditErrorDescription(error, t),
      })
      refreshAfterRefusal(error)
    },
  })

  const isPending = creditIn.isPending || cashOut.isPending

  return (
    <section aria-labelledby="cabinet-credits">
      <h3
        id="cabinet-credits"
        className="mb-2 font-semibold"
      >
        {t('cabinetsPage.credits.heading')}
      </h3>
      <div className="flex flex-wrap gap-2">
        <CreditInDialog
          cabinet={cabinet}
          creditIn={creditIn}
          canOpen={!isPending && canCreditIn}
          disabledReason={disabledReason}
        />
        <ConfirmDialog
          trigger={
            <Button
              variant="outline"
              size="sm"
              disabled={isPending || !canCashOut}
            >
              {t('cabinetsPage.credits.cashOut')}
            </Button>
          }
          title={t('cabinetsPage.credits.cashOutTitle', { name: cabinet.name })}
          description={t('cabinetsPage.credits.cashOutDescription', {
            credits: formatCredits(cabinet.credits),
          })}
          confirmLabel={t('cabinetsPage.credits.cashOut')}
          destructive
          // the balance the dialog shows when the operator confirms
          onConfirm={() => cashOut.mutate(cabinet.credits)}
        />
      </div>
      {disabledReason !== null && (
        <p className="text-muted-foreground mt-2 text-sm">{disabledReason}</p>
      )}
    </section>
  )
}

/**
 * "Add credits": a dialog with the amount to credit in (one of the
 * denominations 0.10 to 100 credits, checked as the operator types). It closes on success; a
 * failed credit-in keeps it open with the reason, and a refusal because the
 * cabinet changed (not idle, not active) re-reads the cabinet, so the dialog
 * shows why it cannot add credits now.
 */
function CreditInDialog({
  cabinet,
  creditIn,
  canOpen,
  disabledReason,
}: {
  cabinet: Cabinet
  creditIn: UseMutationResult<Cabinet, Error, Credits>
  /** the cabinet takes credits now and no other credit change is under way */
  canOpen: boolean
  /** why the cabinet's credits cannot change now (`creditActionsFor`) */
  disabledReason: string | null
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [text, setText] = useState('')
  // no error before the operator typed: an empty field is not a mistake yet
  const [touched, setTouched] = useState(false)
  const inputId = useId()
  const errorId = useId()

  const parsed = parseCreditAmount(text, t)
  const inputError = touched && 'error' in parsed ? parsed.error : null
  const canSubmit =
    'amount' in parsed && disabledReason === null && !creditIn.isPending

  const onOpenChange = (next: boolean) => {
    // no closing while the credit-in runs: its outcome (or refusal) shows
    // here, and a closed dialog would hide it
    if (!next && creditIn.isPending) {
      return
    }
    if (next) {
      setText('')
      setTouched(false)
      creditIn.reset()
    }
    setIsOpen(next)
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setTouched(true)
    if (canSubmit) {
      creditIn.mutate(parsed.amount, { onSuccess: () => setIsOpen(false) })
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={!canOpen}
        >
          {t('cabinetsPage.credits.addCredits')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          noValidate
          onSubmit={onSubmit}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('cabinetsPage.credits.addTitle', { name: cabinet.name })}
            </DialogTitle>
            <DialogDescription>
              {t('cabinetsPage.credits.addDescription', {
                credits: formatCredits(cabinet.credits),
                amounts: creditInAmountsText(),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={inputId}>{t('cabinetsPage.credits.amount')}</Label>
            <Input
              id={inputId}
              inputMode="decimal"
              autoComplete="off"
              autoFocus
              value={text}
              // readOnly, not disabled: a disabled field loses the focus, so
              // after a refusal the operator could not type a fix right away
              readOnly={creditIn.isPending}
              aria-invalid={inputError !== null}
              aria-describedby={inputError === null ? undefined : errorId}
              onChange={(event) => {
                setText(event.target.value)
                setTouched(true)
                if (creditIn.isError) {
                  creditIn.reset()
                }
              }}
            />
            {inputError !== null && (
              <p
                id={errorId}
                className="text-destructive text-sm"
              >
                {inputError}
              </p>
            )}
          </div>
          {disabledReason !== null && (
            <p className="text-muted-foreground text-sm">{disabledReason}</p>
          )}
          {creditIn.isError && (
            <p
              role="alert"
              className="text-destructive text-sm"
            >
              {t('cabinetsPage.credits.creditInFailed', {
                message: creditErrorDescription(creditIn.error, t),
              })}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                disabled={creditIn.isPending}
              >
                {t('shell.common.cancel')}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={!canSubmit}
            >
              {t('cabinetsPage.credits.addCredits')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
